import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'
import IdlePeriodFieldCard from './IdlePeriodFieldCard'
import useModals from 'src/Hooks/useModals'
import { useField, useFormikContext } from 'formik'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { EmsIdlePeriodLegacyFormField } from 'src/Libs/smart-charge/types'

interface Props {
  title: string
  name: string
  index: number
  titleForCharge: string
  titleForDischarge: string
  hasWritingRights?: boolean
}
const DailyIdlePeriodFieldSet: React.VFC<Props> = ({ title, name, index, titleForDischarge, titleForCharge, hasWritingRights = true }) => {
  const fieldSetName = `${name}.${index}`
  const { t } = useCustomTranslation()

  const { openConfirmationModal } = useModals()
  const [mainListField] = useField<EmsIdlePeriodLegacyFormField[]>(name)
  const [chargeIsDirty, setChargeIsDirty] = useState<boolean>(false)
  const [dischargeIsDirty, setDischargeIsDirty] = useState<boolean>(false)

  const { setFieldValue } = useFormikContext()

  const updateWholeSet = useCallback(
    (fieldIndex: number, fieldValues: EmsIdlePeriodLegacyFormField[]): void => {
      const current = fieldValues.find((_, index) => index === fieldIndex, null)
      if (!current) {
        return
      }

      setFieldValue(
        name,
        mainListField.value.map(item => ({ ...current, dayOfWeek: item.dayOfWeek }))
      )
    },
    [mainListField.value, name, setFieldValue]
  )

  const handleChargeChange = useCallback((dirty: boolean) => setChargeIsDirty(dirty), [])
  const handleDischargeChange = useCallback((dirty: boolean) => setDischargeIsDirty(dirty), [])

  const handleAcceptForAll = useCallback(() => {
    openConfirmationModal({
      title: t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.dialogs.acceptAll.title'),
      buttonText: [t('common.cancel'), t('common.confirm')],
      text: t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.dialogs.acceptAll.description'),
      onConfirm: onClose => {
        updateWholeSet(index, mainListField.value)
        onClose()
      },
    })
  }, [index, mainListField.value, openConfirmationModal, t, updateWholeSet])

  return (
    <Box sx={{ pb: 8 }}>
      <Typography variant="h3" sx={{ mt: 2, mb: 2 }}>
        {title}
      </Typography>
      <Grid container>
        <Grid item md={6} xs={12}>
          <IdlePeriodFieldCard
            title={titleForCharge}
            name={`${fieldSetName}.charge`}
            onChange={handleChargeChange}
            hasWritingRights={hasWritingRights}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <IdlePeriodFieldCard
            title={titleForDischarge}
            name={`${fieldSetName}.discharge`}
            onChange={handleDischargeChange}
            hasWritingRights={hasWritingRights}
          />
        </Grid>
      </Grid>
      <Stack direction="row" justifyContent={'end'}>
        <Button onClick={handleAcceptForAll} variant={'text'} disabled={!(chargeIsDirty || dischargeIsDirty || !hasWritingRights)}>
          {t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.controls.acceptForAllDays')}
        </Button>
      </Stack>
    </Box>
  )
}

export default DailyIdlePeriodFieldSet
